import filters from "@/components/filters/Filters";
import { SupervisionTimeCalculation } from "@/models/supervisionTimeCalculations";
import { weekAverageInHoursMinutesFormat } from "@/utils/numberUtils";
import { endOfDay, startOfDay } from "date-fns";
import { Employment } from "../models/employments";
import store from "../store";
import { extendArray } from "./types";
import { PositionCategory } from "@/models/positionCategories";
import { getCurrentCountryConfig } from "@/countries";

@extendArray
export class EmploymentArray extends Array<Employment> {
  constructor(employment: Employment[] | number) {
    if (employment instanceof Array) {
      super(...employment);
    } else {
      super(employment);
    }
    Object.setPrototypeOf(this, Object.create(EmploymentArray.prototype));
  }

  getEmploymentOnDate(
    date: Date,
    employeeid: number | undefined,
  ): Employment | undefined {
    const employmentOnStartDate = this.find(
      (e: Employment) =>
        (!employeeid || e.employee_id === employeeid) &&
        startOfDay(date) >= startOfDay(e.start_date) &&
        date <= endOfDay(e.end_date),
    );
    return employmentOnStartDate;
  }
}

export class EmploymentHandler {
  static getPositionCategory(employment: Employment): PositionCategory {
    return store.getters["positions/GetPositionCategoryByPositionId"](
      employment.position_id,
    );
  }

  static substractSenorityFromNetTime(employment: Employment) {
    if (employment.isNorwegianAgreement()) {
      return false;
    } else if (
      (employment.isDkOK21() &&
        startOfDay(employment.start_date) <
          startOfDay(this.senorityChangeDate)) ||
      employment.soCalculatesAsNotSet
    ) {
      // Besk. der starter før 1/8-2023 skal håndteres på gammel måde (planlagt arbejdstid), også selvom beskæftigelse slutter efter 1/8-2023
      return true;
    } else {
      return false;
    }
  }

  static get senorityChangeDate() {
    return startOfDay(new Date(2023, 7, 1)); // 1/8-2023
  }

  static calculateAverageWorkingHours(
    planned: SupervisionTimeCalculation,
    employment: Employment,
    // Closed days (lukkedage) are deducted from the gross days so the weekly
    // target reflects that those hours must be worked on the remaining open
    // days. Frontend-only; defaults to 0 for callers that don't supply it.
    closedDays = 0,
  ): {
    averageHoursPerDay: string;
    averageHoursPerDayDecimal: number;
    averageHoursPerWeek: string;
    averageHoursPerWeekDecimal: number;
    workHours: number;
    workTimeForPlanning: number;
    workDaysForPlanning: number;
  } {
    const substractSenority = this.substractSenorityFromNetTime(employment);
    const correction = employment.correction ? employment.correction / 60 : 0;
    let workHours = planned?.supervision_hours ?? 0;
    let workDaysForPlanning = 0;
    if (planned) {
      workDaysForPlanning =
        (planned.mon_fri_days ?? 0) -
        closedDays -
        (planned.public_holidays ?? 0);
      if (substractSenority) {
        workDaysForPlanning -= planned.seniority_days ?? 0;
      }
    }

    let averageWorkHoursPerDay = 0;

    let workTimeForPlanning = 0;
    const positionCategory = this.getPositionCategory(employment);

    if (employment.isNorwegianAgreement()) {
      // NO: vacation and seniority do not affect work time. Transferred time
      // (transferred_time_hours) must NOT lower the week target — it counts as
      // flex/time-off-in-lieu, so we add it back to supervision_hours.
      workHours += planned?.transferred_time_hours ?? 0;
      workHours -= correction;
      workTimeForPlanning = workHours;
      averageWorkHoursPerDay =
        workDaysForPlanning > 0 ? workTimeForPlanning / workDaysForPlanning : 0;
    } else if (positionCategory.supervisionExcludesVacation()) {
      workHours -= correction;
      workTimeForPlanning =
        workHours -
        (planned.vacation_hours ? planned.vacation_hours : 0) -
        (planned.sixth_week_hours ? planned.sixth_week_hours : 0);

      workDaysForPlanning =
        workDaysForPlanning -
        (planned.vacation_days ?? 0) -
        (planned.sixth_week_days ?? 0);

      if (employment.isDkOK21() && employment.soCalculatesAsNormDay) {
        workDaysForPlanning -= planned.seniority_days ?? 0;
        workTimeForPlanning -= planned.seniority_hours ?? 0;
      }

      averageWorkHoursPerDay = workTimeForPlanning / workDaysForPlanning;
    } else {
      averageWorkHoursPerDay = workHours / workDaysForPlanning;
    }
    const averageWorkHoursPerDayRounded =
      Math.round(averageWorkHoursPerDay * 100) / 100;
    return {
      averageHoursPerDay: filters.hourDecimalToHourColonMinutes(
        averageWorkHoursPerDayRounded,
      ),
      averageHoursPerDayDecimal: averageWorkHoursPerDay,
      averageHoursPerWeek: weekAverageInHoursMinutesFormat(
        averageWorkHoursPerDayRounded,
      ),
      averageHoursPerWeekDecimal: averageWorkHoursPerDay * 5,
      workHours,
      workTimeForPlanning,
      workDaysForPlanning,
    };
  }

  // Average work hours per day (decimal) for the estimated time-status table
  // (SFOP-7362). Thin wrapper over calculateAverageWorkingHours so the daily
  // target is derived in exactly one place.
  static averageWorkHoursPerDayDecimal(
    planned: SupervisionTimeCalculation,
    employment: Employment,
    // See calculateAverageWorkingHours: closed days (lukkedage) reduce the gross
    // days so the daily/weekly target reflects the remaining open days.
    closedDays = 0,
  ): number {
    return this.calculateAverageWorkingHours(planned, employment, closedDays)
      .averageHoursPerDayDecimal;
  }

  // Converts the employee's remaining vacation/seniority/sixth-week days into
  // hours at the average day rate, used to estimate the realistic rest in the
  // estimated time-status (SFOP-7362). Seniority is only included when it is
  // calculated as norm days.
  static remainingDaysToHours(
    averageWorkHoursPerDay: number,
    remaining: SupervisionTimeCalculation,
    employment: Employment | null,
  ): number {
    const includeSeniority = employment?.soCalculatesAsNormDay ?? true;
    return (
      averageWorkHoursPerDay *
      ((includeSeniority ? (remaining.seniority_days ?? 0) : 0) +
        (remaining.vacation_days ?? 0) +
        (remaining.sixth_week_days ?? 0))
    );
  }

  // Remaining seniority hours valued as norm days (SFOP-7362).
  // When seniority is calculated as norm days the backend zeroes
  // remaining.seniority_hours (it is folded into supervision), so the estimated
  // rest must re-derive it from the remaining seniority days at the norm-day
  // rate (hoursPerDay * rate of employment) — the same valuation the backend
  // uses for remaining vacation/sixth-week hours. Returns 0 when seniority does
  // not count as norm days (then it must not be included at all).
  static remainingSeniorityNormDayHours(
    remaining: SupervisionTimeCalculation,
    employment: Employment | null,
  ): number {
    if (!employment?.soCalculatesAsNormDay) return 0;
    return (
      (remaining.seniority_days ?? 0) *
      getCurrentCountryConfig().hoursPerDay *
      (employment.rate_of_employment ?? 1)
    );
  }

  // Estimated remaining hours ("estimeret rest") at the end of the employment
  // (SFOP-7362): the remaining supervision hours, minus the remaining
  // vacation/sixth-week/seniority hours that are set aside, plus the work hours
  // those remaining days will overwrite when they are actually held. Closed days
  // (lukkedage) reduce the day rate used for the overwrite. Single source of
  // truth shared by useTimeStatusValues and the employee list.
  static estimatedRemainingHours(
    planned: SupervisionTimeCalculation | null,
    remaining: SupervisionTimeCalculation | null,
    employment: Employment | null,
    closedDays = 0,
  ): number {
    if (!remaining) return 0;
    const workHoursOverwritten =
      planned && employment
        ? this.remainingDaysToHours(
            this.averageWorkHoursPerDayDecimal(planned, employment, closedDays),
            remaining,
            employment,
          )
        : 0;
    return (
      (remaining.supervision_hours ?? 0) -
      (remaining.vacation_hours ?? 0) -
      (remaining.sixth_week_hours ?? 0) -
      this.remainingSeniorityNormDayHours(remaining, employment) +
      workHoursOverwritten
    );
  }

  static getFormattedRateOfEmployment(
    value: number,
    hoursPerWeek: number,
  ): string {
    if (value) {
      return hoursPerWeek > 0
        ? (value / hoursPerWeek).toFixed(4).replace(".", ",")
        : "0";
    } else {
      return "0";
    }
  }
}
